import { Factory } from '../interface/factory';

// Main Payment Execution Configuration
export class PaymentExecutionConfiguration {
  cardExecutors: ExecutorConfiguration<CardExecutorConfiguration>[];
  invoiceExecutors: ExecutorConfiguration<InvoiceExecutorConfiguration>[];

  constructor(json: any) {
    const cardFactory = CardExecutorConfiguration.getFactory();
    const invoiceFactory = InvoiceExecutorConfiguration.getFactory();

    this.cardExecutors = json.cardExecutors
      ? json.cardExecutors.map((executor: any) => {
          const config = new ExecutorConfiguration<CardExecutorConfiguration>(executor);
          config.provider = cardFactory.make(executor.provider);
          return config;
        })
      : [];

    this.invoiceExecutors = json.invoiceExecutors
      ? json.invoiceExecutors.map((executor: any) => {
          const config = new ExecutorConfiguration<InvoiceExecutorConfiguration>(executor);
          config.provider = invoiceFactory.make(executor.provider);
          return config;
        })
      : [];
  }
}

// Base class for executor configurations
export class ExecutorConfiguration<T> {
  id: string;
  active: boolean;
  provider: T;

  constructor(json: any) {
    this.id = json.id;
    this.active = json.active ? Boolean(json.active) : false;
    this.provider = json.provider;
  }

  hasSameConfig(other: ExecutorConfiguration<T>): boolean {
    return this.active === other.active && JSON.stringify(this.provider) === JSON.stringify(other.provider);
  }
}

export enum CardExecutorConfigurationType {
  Worldline = 'Worldline',
}

// Base class for card executor configurations
export class CardExecutorConfiguration {
  type: CardExecutorConfigurationType;

  constructor(json: any) {
    this.type = json.type as CardExecutorConfigurationType;
  }

  static getFactory(): Factory<CardExecutorConfiguration> {
    return new (class implements Factory<CardExecutorConfiguration> {
      make(json: any): CardExecutorConfiguration {
        switch (json.type) {
          case CardExecutorConfigurationType.Worldline:
            return new WorldlineCardExecutorConfiguration(json);
          default:
            throw new Error(`Unrecognized card executor configuration type: ${json.type}`);
        }
      }
      getTableName(): string {
        throw new Error('CardExecutorConfiguration is never stored in IndexedDB.');
      }
    })();
  }
}

export class WorldlineCardExecutorConfigurationUrls {
  acceptUrl: string;
  cancelUrl: string;

  constructor(json: any) {
    this.acceptUrl = json.acceptUrl;
    this.cancelUrl = json.cancelUrl;
  }
}

export class WorldlineCardExecutorConfiguration extends CardExecutorConfiguration {
  urls: WorldlineCardExecutorConfigurationUrls;

  constructor(json: any) {
    super(json);
    this.urls = new WorldlineCardExecutorConfigurationUrls(json.urls);
  }
}

// Invoice Executor Configuration Types
export enum InvoiceExecutorConfigurationType {
  Postnord = 'Postnord',
  Orbyt = 'Orbyt',
  ManualEmail = 'ManualEmail',
}

// Base class for invoice executor configurations
export class InvoiceExecutorConfiguration {
  type: InvoiceExecutorConfigurationType;

  constructor(json: any) {
    this.type = json.type;
  }

  static getFactory(): Factory<InvoiceExecutorConfiguration> {
    return new (class implements Factory<InvoiceExecutorConfiguration> {
      make(json: any): InvoiceExecutorConfiguration {
        switch (json.type) {
          case InvoiceExecutorConfigurationType.Postnord:
            return new PostnordInvoiceExecutorConfiguration(json);
          case InvoiceExecutorConfigurationType.Orbyt:
            return new OrbytInvoiceExecutorConfiguration(json);
          case InvoiceExecutorConfigurationType.ManualEmail:
            return new ManualEmailInvoiceExecutorConfiguration(json);
          default:
            throw new Error(`Unrecognized invoice executor configuration type: ${json.type}`);
        }
      }
      getTableName(): string {
        throw new Error('InvoiceExecutorConfiguration is never stored in IndexedDB.');
      }
    })();
  }
}

export class PostnordInvoiceExecutorConfiguration extends InvoiceExecutorConfiguration {
  constructor(json: any) {
    super(json);
  }
}

export class OrbytInvoiceExecutorConfiguration extends InvoiceExecutorConfiguration {
  constructor(json: any) {
    super(json);
  }
}

export class ManualEmailChannelConfiguration {
  templateId: string;
  subject: string;

  constructor(json: any) {
    this.templateId = json.templateId;
    this.subject = json.subject;
  }
}

export class ManualEmailInvoiceExecutorConfiguration extends InvoiceExecutorConfiguration {
  channelConfiguration: ManualEmailChannelConfiguration;

  constructor(json: any) {
    super(json);
    this.channelConfiguration = json.channelConfiguration;
  }
}
